import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Ban, CalendarClock, Check, Plus } from 'lucide-react';
import { Botao, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { useToast } from '../../componentes/toast';
import { getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { dataHora, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Atividade, Oportunidade } from './tipos';
import { DialogoConcluir } from './ConcluirAtividades';
import { DialogoJustificativa } from './DialogoJustificativa';
import { FormTarefa, rascunhoInicial } from './FormTarefa';

const COR_ESTADO: Record<Atividade['estado'], CorSelo> = { pendente: 'azul', concluida: 'verde', cancelada: 'neutro' };

/** Lista as atividades da oportunidade no drawer, com concluir e cancelar. */
export function AtividadesDaOportunidade({ oportunidade }: { oportunidade: Oportunidade }) {
  const { t } = useTranslation('pipeline');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [novo, setNovo] = useState(false);
  const aberta = oportunidade.estado === 'aberta';
  const q = useQuery({
    queryKey: ['pipeline', 'atividades-da-oportunidade', oportunidade.id],
    queryFn: () => getTodos<Atividade>('/pipeline/atividades', { oportunidade_id: oportunidade.id, filtro: 'todas' }),
  });
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: ['pipeline'] });
    await qc.invalidateQueries({ queryKey: ['/pipeline/atividades'] });
    await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', oportunidade.id] });
  };
  // CRM lote 2: concluir exige a ata (a mesma janela da página Atividades do CRM).
  const [concluir, setConcluir] = useState<Atividade | null>(null);
  // Lista B de 30/10 (item 13): cancelar pede a justificativa.
  const [cancelar, setCancelar] = useState<Atividade | null>(null);
  const acao = useMutation({
    mutationFn: (v: { id: string; justificativa: string }) => post(`/pipeline/atividades/${v.id}/cancelar`, { justificativa: v.justificativa }),
    onSuccess: async () => {
      setCancelar(null);
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <section aria-label={t('drawer.atividades')}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{t('drawer.atividades')}</h3>
        {aberta && s.pode('pipeline.criar') && (
          <Botao tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setNovo(true)}>
            {t('acoes.agendar')}
          </Botao>
        )}
      </div>
      <Estado consulta={q}>
        {(d) =>
          d.itens.length === 0 ? (
            <p className="text-sm text-tinta-3">{t('drawer.nenhumaAtividade')}</p>
          ) : (
          <ul className="divide-y divide-borda rounded-lg border border-borda">
            {d.itens.map((a) => (
              <li key={a.id} className="flex items-start justify-between gap-3 px-3 py-2.5 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-medium" title={textoLivre(a.titulo, oportunidade.parceiro_nome)}>{textoLivre(a.titulo, oportunidade.parceiro_nome)}</p>
                  <p className={`flex flex-wrap items-center gap-x-2 text-xs ${a.atrasada ? 'text-laranja' : 'text-tinta-3'}`}>
                    <CalendarClock className="size-3.5" aria-hidden />
                    <span className="numero">{dataHora(a.data_hora)}</span>
                    <span>{t(`tipoAtividade.${a.tipo}`)}</span>
                    {/* Responsável é usuário do sistema: fica com o nome (decisão r9; r10 visual B6). */}
                    {a.responsavel_nome && <span>{a.responsavel_nome}</span>}
                    {a.agendamento_id && <span>{t('drawer.agendamento')}</span>}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {a.estado === 'pendente' && s.pode('pipeline.editar') ? (
                    <>
                      <Botao tamanho="sm" variante="fantasma" aria-label={t('acoes.concluir')} title={t('acoes.concluir')} ocupado={acao.isPending} onClick={() => setConcluir(a)} icone={<Check className="size-4" />} />
                      {(!a.agendamento_id || s.pode('agenda.cancelar')) && <Botao tamanho="sm" variante="fantasma" aria-label={t('acoes.cancelar')} title={t('acoes.cancelar')} ocupado={acao.isPending} onClick={() => setCancelar(a)} icone={<Ban className="size-4" />} />}
                    </>
                  ) : (
                    <Selo cor={COR_ESTADO[a.estado]}>{t(`estadoAtividade.${a.estado}`)}</Selo>
                  )}
                </div>
              </li>
            ))}
          </ul>
          )
        }
      </Estado>
      {concluir && <DialogoConcluir atividades={[concluir]} aoFechar={() => setConcluir(null)} />}
      <DialogoJustificativa
        aberto={!!cancelar}
        aoFechar={() => setCancelar(null)}
        aoConfirmar={(justificativa) => cancelar && acao.mutate({ id: cancelar.id, justificativa })}
        carregando={acao.isPending}
        titulo={t('crm.form.confirmarCancelar')}
        texto={t('crm.form.confirmarCancelarTexto')}
        botao={t('crm.form.cancelarTarefa')}
        nomesNoTexto={[oportunidade.parceiro_nome]}
      />
      {/* Ficha 2026-11-03: um formulário só. O antigo pedia recurso e serviço; a tarefa do tipo Reunião agenda pelos campos livres. */}
      {novo && (
        <FormTarefa
          travarNegociacao
          titulo={t('modal.agendarTitulo')}
          inicial={rascunhoInicial(s.usuario.id, { parceiroId: oportunidade.parceiro_id, parceiroNome: oportunidade.parceiro_nome, oportunidadeId: oportunidade.id, oportunidadeTitulo: oportunidade.titulo })}
          aoFechar={() => setNovo(false)}
          aoSalvo={() => void recarregar()}
        />
      )}
    </section>
  );
}
